/* eslint-disable callback-return */
/* eslint-disable no-duplicate-imports */
import axios, { isAxiosError } from "axios";
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig
} from "axios";

// --- Custom Error Class ---

/**
 * Custom error class for HTTP requests that provides additional context
 * and helper methods for error handling.
 *
 * @example
 * ```typescript
 * try {
 *   await httpService.request({ url: "/api/data" });
 * } catch (error) {
 *   if (error instanceof HttpServiceError) {
 *     if (error.isUnauthorized()) {
 *       // Handle 401
 *     }
 *     console.log(error.status, error.url, error.method);
 *   }
 * }
 * ```
 */
export class HttpServiceError extends Error {
  /** HTTP status code (e.g., 401, 404, 500) */
  public readonly status?: number;
  /** The URL that was requested */
  public readonly url?: string;
  /** HTTP method used (GET, POST, etc.) */
  public readonly method?: string;
  /** Axios error code (e.g., ERR_NETWORK, ECONNABORTED) */
  public readonly code?: string;
  /** Original Axios response object */
  public readonly response?: AxiosResponse;

  constructor(error: AxiosError) {
    const message = error.response?.data ?
      JSON.stringify(error.response.data) :
      error.message;

    super(message);
    this.name = "HttpServiceError";
    this.status = error.response?.status;
    this.url = error.config?.url;
    this.method = error.config?.method?.toUpperCase();
    this.code = error.code;
    this.response = error.response;
  }

  /** Returns true if the error is a network error (no connection) */
  public isNetworkError(): boolean {
    return this.code === "ERR_NETWORK";
  }

  /** Returns true if the request timed out */
  public isTimeoutError(): boolean {
    return this.code === "ECONNABORTED";
  }

  /** Returns true if the response status is 401 */
  public isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** Returns true if the response status is 403 */
  public isForbidden(): boolean {
    return this.status === 403;
  }

  /** Returns true if the response status is 404 */
  public isNotFound(): boolean {
    return this.status === 404;
  }

  /** Returns true if the response status is 5xx */
  public isServerError(): boolean {
    return this.status !== undefined && this.status >= 500;
  }

  /** Returns true if the request was cancelled */
  public isCancelled(): boolean {
    return this.code === "ERR_CANCELED";
  }

  /** Returns true if the error occurred because the device is offline */
  public isOffline(): boolean {
    return this.code === "ERR_OFFLINE";
  }
}

/**
 * Custom error for offline scenarios.
 */
export class OfflineError extends HttpServiceError {
  constructor() {
    const axiosError = {
      message: "No internet connection",
      code: "ERR_OFFLINE",
      name: "AxiosError",
      isAxiosError: true,
      toJSON: () => ({})
    } as AxiosError;

    super(axiosError);
    this.name = "OfflineError";
  }
}

// --- Types ---

/**
 * Extended request configuration with additional HttpService options.
 */
interface HttpServiceRequestConfig extends AxiosRequestConfig {
  /** URL path variables to replace (e.g., { id: 123 } replaces {id} in URL) */
  urlVariables?: Record<string, string | number>;
  /** Number of retry attempts for failed requests (default: 0) */
  retry?: number;
  /** Initial delay between retries in ms, doubles each attempt (default: 1000) */
  retryDelay?: number;
  /** Skip transforming AxiosError to HttpServiceError */
  skipErrorTransform?: boolean;
  /** Unique key for request deduplication - concurrent requests with same key return same promise */
  dedupeKey?: string;
  /** Unique key for request cancellation - allows cancelling this request via cancelRequest(key) */
  requestKey?: string;
}

/**
 * Configuration options for HttpService instance.
 */
interface HttpServiceConfig extends AxiosRequestConfig {
  /** Transform AxiosError to HttpServiceError (default: true) */
  enableErrorTransform?: boolean;
  /** Enable debug logging for requests/responses (default: false) */
  debug?: boolean;
  /** Custom logger function for debug mode */
  logger?: (message: string, data?: unknown) => void;
  /** Default timeout for all requests in ms (default: 30000) */
  defaultTimeout?: number;
  /** Enable offline detection - rejects requests immediately when offline (default: false) */
  enableOfflineDetection?: boolean;
}

// --- HttpService Class ---

/**
 * A robust HTTP client wrapper around Axios that provides:
 * - Request cancellation and tracking
 * - Request deduplication
 * - Automatic retry with exponential backoff
 * - URL variable substitution
 * - Custom error handling
 * - Debug logging
 * - Interceptor management with keys
 *
 * @example
 * ```typescript
 * // Create an instance
 * const http = new HttpService({
 *   baseURL: "https://api.example.com",
 *   defaultTimeout: 10000,
 *   debug: true
 * });
 *
 * // Basic request
 * const response = await http.request({
 *   url: "/users/{id}",
 *   method: "GET",
 *   urlVariables: { id: 123 }
 * });
 *
 * // Cancellable request (useful for search/autocomplete)
 * const results = await http.request({
 *   url: "/search",
 *   params: { q: searchTerm },
 *   requestKey: "search" // New request with same key cancels previous
 * });
 *
 * // Request with retry
 * const data = await http.request({
 *   url: "/unstable-endpoint",
 *   retry: 3,
 *   retryDelay: 1000
 * });
 *
 * // Deduplicated request (concurrent calls return same promise)
 * const userPromise = http.request({
 *   url: "/user/profile",
 *   dedupeKey: "user-profile"
 * });
 * ```
 */
class HttpService {
  private instance: AxiosInstance;
  private requestInterceptors: Map<string, number>;
  private responseInterceptors: Map<string, number>;
  private pendingRequests: Map<string, AbortController>;
  private inFlightRequests: Map<string, Promise<unknown>>;
  private enableErrorTransform: boolean;
  private enableOfflineDetection: boolean;
  private debug: boolean;
  private logger: (message: string, data?: unknown) => void;
  private defaultTimeout: number;
  private onlineStatusListeners: Set<(isOnline: boolean) => void>;
  private boundHandleOnline: () => void;
  private boundHandleOffline: () => void;

  /**
   * Creates a new HttpService instance.
   *
   * @param config - Configuration options extending AxiosRequestConfig
   * @param config.enableErrorTransform - Transform errors to HttpServiceError (default: true)
   * @param config.debug - Enable debug logging (default: false)
   * @param config.logger - Custom logger function (default: console.log)
   * @param config.defaultTimeout - Default request timeout in ms (default: 30000)
   * @param config.enableOfflineDetection - Reject requests when offline (default: false)
   */
  constructor(config: HttpServiceConfig = {}) {
    const {
      enableErrorTransform = true,
      enableOfflineDetection = false,
      debug = false,
      logger = console.log,
      defaultTimeout = 30000,
      ...axiosConfig
    } = config;

    this.instance = axios.create({
      timeout: defaultTimeout,
      ...axiosConfig
    });
    this.requestInterceptors = new Map();
    this.responseInterceptors = new Map();
    this.pendingRequests = new Map();
    this.inFlightRequests = new Map();
    this.enableErrorTransform = enableErrorTransform;
    this.enableOfflineDetection = enableOfflineDetection;
    this.debug = debug;
    this.logger = logger;
    this.defaultTimeout = defaultTimeout;
    this.onlineStatusListeners = new Set();

    // Bind event handlers for proper cleanup
    this.boundHandleOnline = this.handleOnlineStatusChange.bind(this, true);
    this.boundHandleOffline = this.handleOnlineStatusChange.bind(this, false);

    // Setup online/offline event listeners (browser only)
    if (typeof window !== "undefined" && enableOfflineDetection) {
      window.addEventListener("online", this.boundHandleOnline);
      window.addEventListener("offline", this.boundHandleOffline);
    }
  }

  // --- Core Request Method ---

  /**
   * Makes an HTTP request with support for retries, deduplication, and URL variables.
   *
   * @template T - Expected response data type
   * @template R - Full response type (default: AxiosResponse<T>)
   * @param config - Request configuration
   * @returns Promise resolving to the response
   * @throws {HttpServiceError} When request fails (if enableErrorTransform is true)
   * @throws {OfflineError} When offline detection is enabled and device is offline
   *
   * @example
   * ```typescript
   * // GET request with URL variables
   * const response = await http.request<User>({
   *   url: "/users/{id}",
   *   method: "GET",
   *   urlVariables: { id: 123 }
   * });
   *
   * // POST request with retry
   * const response = await http.request<CreateResponse>({
   *   url: "/orders",
   *   method: "POST",
   *   data: { items: [...] },
   *   retry: 2
   * });
   * ```
   */
  public request = async <T = unknown, R = AxiosResponse<T>>(
    config: HttpServiceRequestConfig
  ): Promise<R> => {
    // Check offline status before making request
    if (this.enableOfflineDetection && !this.isOnline()) {
      this.log("Request blocked - device is offline", { url: config.url });
      throw new OfflineError();
    }

    const {
      retry = 0,
      retryDelay = 1000,
      skipErrorTransform,
      dedupeKey,
      requestKey,
      ...restConfig
    } = config;

    // Handle request deduplication
    if (dedupeKey) {
      const existingRequest = this.inFlightRequests.get(dedupeKey);
      if (existingRequest) {
        this.log("Deduplicating request", {
          dedupeKey,
          url: config.url
        });
        return existingRequest as Promise<R>;
      }
    }

    // Setup cancellation if requestKey is provided
    let controller: AbortController | undefined;
    if (requestKey) {
      // Cancel any existing request with the same key
      this.cancelRequest(requestKey);
      controller = new AbortController();
      this.pendingRequests.set(requestKey, controller);
    }

    const finalConfig = this.processUrlVariables({
      ...restConfig,
      ...(controller && { signal: controller.signal })
    });

    const requestPromise = this.executeRequest<T, R>(finalConfig, {
      retry,
      retryDelay,
      skipErrorTransform,
      dedupeKey,
      requestKey,
      ...config
    });

    // Store promise for deduplication
    if (dedupeKey) {
      this.inFlightRequests.set(dedupeKey, requestPromise);
      requestPromise.finally(() => {
        this.inFlightRequests.delete(dedupeKey);
      });
    }

    // Clean up pending request when done
    if (requestKey) {
      requestPromise.finally(() => {
        // Only delete if it's still our controller (not replaced by a newer request)
        if (this.pendingRequests.get(requestKey) === controller) {
          this.pendingRequests.delete(requestKey);
        }
      });
    }

    return requestPromise;
  };

  private executeRequest = async <T = unknown, R = AxiosResponse<T>>(
    finalConfig: AxiosRequestConfig,
    originalConfig: HttpServiceRequestConfig
  ): Promise<R> => {
    const { retry = 0, retryDelay = 1000, skipErrorTransform } = originalConfig;
    const startTime = Date.now();

    this.log("Request started", {
      method: finalConfig.method || "GET",
      url: finalConfig.url,
      params: finalConfig.params
    });

    try {
      const response = await this.instance.request<T, R>(finalConfig);

      this.log("Request completed", {
        method: finalConfig.method || "GET",
        url: finalConfig.url,
        status: (response as AxiosResponse).status,
        duration: `${Date.now() - startTime}ms`
      });

      return response;
    }
    catch (error) {
      if (isAxiosError(error)) {
        this.log("Request failed", {
          method: finalConfig.method || "GET",
          url: finalConfig.url,
          status: error.response?.status,
          code: error.code,
          duration: `${Date.now() - startTime}ms`
        });

        // Don't retry cancelled requests
        if (error.code === "ERR_CANCELED") {
          if (this.enableErrorTransform && !skipErrorTransform) {
            throw new HttpServiceError(error);
          }
          throw error;
        }

        // Retry logic for network/timeout errors
        if (retry > 0 && this.shouldRetry(error)) {
          this.log("Retrying request", {
            url: finalConfig.url,
            remainingRetries: retry - 1,
            nextDelay: `${retryDelay}ms`
          });

          await this.delay(retryDelay);
          return this.executeRequest<T, R>(finalConfig, {
            ...originalConfig,
            retry: retry - 1,
            retryDelay: retryDelay * 2 // Exponential backoff
          });
        }

        // Transform to custom error
        if (this.enableErrorTransform && !skipErrorTransform) {
          throw new HttpServiceError(error);
        }
      }
      throw error;
    }
  };

  private shouldRetry(error: AxiosError): boolean {
    // Retry on network errors, timeouts, or 5xx server errors
    const isNetworkError = error.code === "ERR_NETWORK";
    const isTimeout = error.code === "ECONNABORTED";
    const isServerError =
      error.response?.status !== undefined && error.response.status >= 500;

    return isNetworkError || isTimeout || isServerError;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private log(message: string, data?: unknown): void {
    if (this.debug) {
      this.logger(`[HttpService] ${message}`, data);
    }
  }

  // --- Request Cancellation ---

  /**
   * Cancels a pending request by its key.
   *
   * @param requestKey - The key of the request to cancel
   */
  public cancelRequest = (requestKey: string): void => {
    const controller = this.pendingRequests.get(requestKey);
    if (controller) {
      this.log("Cancelling request", { requestKey });
      controller.abort();
      this.pendingRequests.delete(requestKey);
    }
  };

  /**
   * Cancels all pending requests. Useful for cleanup on logout or unmount.
   */
  public cancelAllRequests = (): void => {
    this.log("Cancelling all requests", { count: this.pendingRequests.size });
    this.pendingRequests.forEach((controller) => controller.abort());
    this.pendingRequests.clear();
  };

  /**
   * Checks if a request with the given key is currently pending.
   *
   * @param requestKey - The key to check
   * @returns true if the request is pending
   */
  public hasPendingRequest = (requestKey: string): boolean => {
    return this.pendingRequests.has(requestKey);
  };

  /**
   * Gets the count of currently pending requests.
   *
   * @returns Number of pending requests
   */
  public getPendingRequestCount = (): number => {
    return this.pendingRequests.size;
  };

  // --- URL Variable Processing ---

  private processUrlVariables = (
    config: HttpServiceRequestConfig
  ): AxiosRequestConfig => {
    const { urlVariables, ...restConfig } = config;

    if (!urlVariables || !config.url) {
      return restConfig;
    }

    let url = String(config.url);

    Object.entries(urlVariables).forEach(([ key, value ]) => {
      const placeholder = `{${key}}`;
      if (!url.includes(placeholder)) {
        console.warn(
          `[HttpService] URL variable "${key}" not found in URL: ${url}`
        );
      }
      url = url.replace(placeholder, encodeURIComponent(String(value)));
    });

    // Warn if there are remaining placeholders
    const remainingPlaceholders = url.match(/\{[^}]+\}/g);
    if (remainingPlaceholders) {
      console.warn(
        `[HttpService] Unresolved URL variables: ${remainingPlaceholders.join(
          ", "
        )}`
      );
    }

    return {
      ...restConfig,
      url
    };
  };

  // --- Header Management ---

  /**
   * Sets a default header for all requests.
   *
   * @param key - Header name
   * @param value - Header value
   */
  public setHeader = (key: string, value: string): void => {
    this.instance.defaults.headers.common[key] = value;
  };

  /**
   * Gets a default header value.
   *
   * @param key - Header name
   * @returns Header value or undefined
   */
  public getHeader = (key: string): string | undefined => {
    const value = this.instance.defaults.headers.common[key];
    return typeof value === "string" ? value : undefined;
  };

  /**
   * Removes a default header.
   *
   * @param key - Header name to remove
   */
  public removeHeader = (key: string): void => {
    delete this.instance.defaults.headers.common[key];
  };

  // --- Interceptor Management ---

  /**
   * Adds a request interceptor with a unique key. If an interceptor with the
   * same key exists, it will be removed first.
   *
   * @param key - Unique identifier for this interceptor
   * @param onFulfilled - Function to transform the request config
   * @param onRejected - Function to handle request errors
   */
  public addRequestInterceptor = (
    key: string,
    onFulfilled?: (
      config: InternalAxiosRequestConfig
    ) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>,
    onRejected?: (error: unknown) => unknown
  ): void => {
    this.ejectRequestInterceptor(key);
    const id = this.instance.interceptors.request.use(onFulfilled, onRejected);
    this.requestInterceptors.set(key, id);
    this.log("Added request interceptor", { key });
  };

  /**
   * Adds a response interceptor with a unique key. If an interceptor with the
   * same key exists, it will be removed first.
   *
   * @param key - Unique identifier for this interceptor
   * @param onFulfilled - Function to transform the response
   * @param onRejected - Function to handle response errors
   */
  public addResponseInterceptor = (
    key: string,
    onFulfilled?: (
      response: AxiosResponse
    ) => AxiosResponse | Promise<AxiosResponse>,
    onRejected?: (error: unknown) => unknown
  ): void => {
    this.ejectResponseInterceptor(key);
    const id = this.instance.interceptors.response.use(onFulfilled, onRejected);
    this.responseInterceptors.set(key, id);
    this.log("Added response interceptor", { key });
  };

  /**
   * Removes a request interceptor by its key.
   *
   * @param key - The interceptor key to remove
   */
  public ejectRequestInterceptor = (key: string): void => {
    const id = this.requestInterceptors.get(key);
    if (id !== undefined) {
      this.instance.interceptors.request.eject(id);
      this.requestInterceptors.delete(key);
      this.log("Ejected request interceptor", { key });
    }
  };

  /**
   * Removes a response interceptor by its key.
   *
   * @param key - The interceptor key to remove
   */
  public ejectResponseInterceptor = (key: string): void => {
    const id = this.responseInterceptors.get(key);
    if (id !== undefined) {
      this.instance.interceptors.response.eject(id);
      this.responseInterceptors.delete(key);
      this.log("Ejected response interceptor", { key });
    }
  };

  // --- Utility Methods ---

  /**
   * Returns the underlying Axios instance for advanced use cases.
   *
   * @returns The Axios instance
   */
  public getAxiosInstance = (): AxiosInstance => {
    return this.instance;
  };

  /**
   * Gets the configured base URL.
   *
   * @returns Base URL or undefined
   */
  public getBaseUrl = (): string | undefined => {
    return this.instance.defaults.baseURL;
  };

  /**
   * Gets the default timeout value.
   *
   * @returns Timeout in milliseconds
   */
  public getDefaultTimeout = (): number => {
    return this.defaultTimeout;
  };

  /**
   * Enables or disables debug mode at runtime.
   *
   * @param enabled - Whether debug mode should be enabled
   */
  public setDebugMode = (enabled: boolean): void => {
    this.debug = enabled;
  };

  /**
   * Checks if debug mode is enabled.
   *
   * @returns true if debug mode is enabled
   */
  public isDebugMode = (): boolean => {
    return this.debug;
  };

  // --- Offline Detection ---

  /**
   * Checks if the device is currently online.
   * In non-browser environments, always returns true.
   *
   * @returns true if online, false if offline
   */
  public isOnline = (): boolean => {
    if (typeof navigator !== "undefined" && "onLine" in navigator) {
      return navigator.onLine;
    }
    return true; // Assume online in non-browser environments
  };

  /**
   * Checks if the device is currently offline.
   *
   * @returns true if offline, false if online
   */
  public isOffline = (): boolean => {
    return !this.isOnline();
  };

  /**
   * Registers a listener for online/offline status changes.
   * The callback will be called with `true` when coming online
   * and `false` when going offline.
   *
   * @param callback - Function to call when online status changes
   * @returns Unsubscribe function to remove the listener
   *
   * @example
   * ```typescript
   * const unsubscribe = http.onOnlineStatusChange((isOnline) => {
   *   if (isOnline) {
   *     console.log("Back online!");
   *     // Retry failed requests, sync data, etc.
   *   } else {
   *     console.log("Gone offline");
   *     // Show offline banner, queue requests, etc.
   *   }
   * });
   *
   * // Later, to stop listening:
   * unsubscribe();
   * ```
   */
  public onOnlineStatusChange = (
    callback: (isOnline: boolean) => void
  ): (() => void) => {
    this.onlineStatusListeners.add(callback);

    return () => {
      this.onlineStatusListeners.delete(callback);
    };
  };

  /**
   * Waits for the device to come back online.
   * Resolves immediately if already online.
   *
   * @param timeout - Maximum time to wait in ms (optional, no timeout by default)
   * @returns Promise that resolves when online
   * @throws Error if timeout is reached while still offline
   *
   * @example
   * ```typescript
   * try {
   *   await http.waitForOnline(30000); // Wait up to 30 seconds
   *   await http.request({ url: "/api/sync" });
   * } catch (error) {
   *   console.log("Still offline after 30 seconds");
   * }
   * ```
   */
  public waitForOnline = (timeout?: number): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (this.isOnline()) {
        resolve();
        return;
      }

      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      const unsubscribe = this.onOnlineStatusChange((isOnline) => {
        if (isOnline) {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          unsubscribe();
          resolve();
        }
      });

      if (timeout !== undefined) {
        timeoutId = setTimeout(() => {
          unsubscribe();
          reject(
            new Error(`Timeout waiting for online status after ${timeout}ms`)
          );
        }, timeout);
      }
    });
  };

  private handleOnlineStatusChange = (isOnline: boolean): void => {
    this.log(`Online status changed: ${isOnline ? "online" : "offline"}`);
    this.onlineStatusListeners.forEach((callback) => {
      try {
        callback(isOnline);
      }
      catch (error) {
        console.error("[HttpService] Error in online status listener:", error);
      }
    });
  };

  /**
   * Cleans up event listeners. Call this when the HttpService instance
   * is no longer needed to prevent memory leaks.
   */
  public destroy = (): void => {
    if (typeof window !== "undefined") {
      window.removeEventListener("online", this.boundHandleOnline);
      window.removeEventListener("offline", this.boundHandleOffline);
    }
    this.onlineStatusListeners.clear();
    this.cancelAllRequests();
    this.log("HttpService destroyed");
  };
}

export default HttpService;
export type { HttpServiceRequestConfig, HttpServiceConfig };
